import { ForwardRefExoticComponent, RefAttributes, SVGProps } from "react";
import Link from "next/link";
import {
  ColDef,
  HeaderValueGetterParams,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "@ag-grid-community/core";
import capitalize from "just-capitalize";

import { DateText, FinancialText } from "@fsg/gui-bits";
import {
  Calendar,
  Coffee,
  Edit3,
  Eye,
  FileTextCheck,
  Fire,
  Jollyroger,
  Mail,
  Maximize2,
  PhoneCall,
  Snowflake,
} from "@fsg/icons";
import { cn, formatPhoneNumber } from "@fsg/utils";

import { CellListWithPopover } from "@app/components/GridCell/CellListWithPopover";
import {
  AccountResponse,
  ActivityResponse,
  CampaignEnums,
  CampaignResponse,
  ContactResponse,
  Division,
  ENTITY,
  EntityType,
  ExpenseResponse,
  FSGServices,
  JobFunction,
  LeadResponse,
  LeadStatus,
  ManagementPreferenceResponse,
  MarketingGroup,
  MarketingSource,
  MarketingSummaryResponse,
  OpportunitiesEnums,
  OpportunityResponse,
  QualityRating,
  SingularEntityType,
  SiteOption,
  SiteResponse,
  StateResponse,
} from "@app/types";

import {
  divisionFormatter,
  formatCampaignFilterSet,
  normalizeENUM,
  statusFormatter,
} from "./formUtilities";
import { RouteFunction, routes } from "./routes";
import { placeholder } from "./usePlaceholder";
import { convertFilterModelToApiQuery, formatStringOption } from "@app/utils";
import { Flame, LucideCoffee, LucideSnowflake, Skull } from "lucide-react";
import { FetchOptions } from "@fsg/next-auth";
import { ENDPOINTS } from "@app/constants";

//----------------------------------------------------------------
//Component Default Configs

//this is spread on every appropriate component in this file. If you need to override any config individually, you can do it at the colDef declaration.
const dateProps = {
  hideTime: true,
  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

const financialProps = {
  decimals: 2,
};

//----------------------------------------------------------------
//References, Helper Functions, Types & Interfaces

const currentYear = new Date().getFullYear();
const prevYear = currentYear - 1;

type ParseValueProps = {
  value: any;
  component?: React.ReactNode;
};

async function getDistinctColumnValues(
  params: any,
  entity: ENTITY,
  columnName: string,
  requestFn: <APIResponseType>({
    url,
    path,
    auth,
    refresh,
    ...options
  }?: FetchOptions) => Promise<APIResponseType>
) {
  const filterModel = params.api.getFilterModel();
  delete filterModel[columnName];
  const filterModelParam = convertFilterModelToApiQuery(filterModel) || "";
  const url = ENDPOINTS.UNIQUE_COLUMNS(entity, columnName, filterModelParam);
  try {
    const response = await requestFn({ path: url });
    return response;
  } catch (error) {
    console.error("Error fetching distinct column values:", error);
    return [];
  }
}

export const parseValue = ({ value, component }: ParseValueProps) => {
  const isEmptyString = (val: any) =>
    typeof val === "string" && val.trim().length === 0;

  const isEmptyArray = (val: any) => Array.isArray(val) && val.length === 0;

  const isEmptyObject = (val: any) =>
    typeof val === "object" && Object.keys(val).length === 0;
  const isEmpty =
    value === undefined ||
    value === null ||
    isEmptyString(value) ||
    isEmptyArray(value) ||
    isEmptyObject(value);
  if (isEmpty) return placeholder;
  return component || value;
};

const numericSort = (valueA: number, valueB: number) => {
  return parseFloat(`${valueA}`) ?? 0 - parseFloat(`${valueB}`) ?? 0;
};

function noSortComparator(a: any, b: any) {
  return 0;
}
interface SVGRProps {
  title?: string;
  titleId?: string;
}
const QUALITY_LOOKUP: {
  [key: string]: {
    icon: ForwardRefExoticComponent<
      Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
    >;
    classes: string;
  };
} = {
  Hot: { icon: Flame, classes: "bg-red-100 text-red-800" },
  Warm: { icon: LucideCoffee, classes: "bg-yellow-100 text-yellow-800" },
  Cold: { icon: LucideSnowflake, classes: "bg-blue-100 text-blue-800" },
  Dead: { icon: Skull, classes: "bg-gray-200 text-gray-900" },
};
function StatusIconLink({
  data,
  route,
}: {
  data: LeadResponse | OpportunityResponse;
  route: RouteFunction;
}) {
  const quality = QUALITY_LOOKUP[data.quality?.rating];
  if (quality === undefined) return null;
  const { icon: Icon, classes } = quality;
  return (
    <Link
      href={''}
      className={cn(classes, "rounded-xs pb-2xs pt-4xs px-2xs pointer-events-none")}     
    >
      <Icon className={cn("inline w-[14px]")} />
    </Link>
  );
}

//----------------------------------------------------------------

export abstract class GridColDef {
  static division(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    divisions: Division[],
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "division",
      headerName: "Division",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          const values = await getDistinctColumnValues(
            params,
            entity,
            "division",
            requestFn
          );
          if (Array.isArray(values)) {
            const displayValues = values
              .filter((value) => value != null && value !== "")
              .map((value) => value);
            params.success(displayValues);
          } else {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
        cellRenderer: (params: any) => {
          return divisionFormatter({ value: params.value }, divisions);
        },
      },
      valueFormatter: (
        params: ValueFormatterParams<
          AccountResponse | LeadResponse | ContactResponse
        >
      ) => {
        return divisionFormatter(params, divisions);
      },
      cellRenderer: (params: ICellRendererParams) => {
        return parseValue({
          value: params.value,
          component: <>{divisionFormatter(params, divisions)}</>,
        });
      },
      sortable: true,
      ...colProps,
    };
  }

  static notes(
    handleNotesClick: (leadId: string, leadName: string) => void,
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: 'notes',
      headerName: '',
      maxWidth: 35,
      width: 35,
      filter: false,      
      sortable: false,
      suppressMenu: true,
      cellClass: "!flex !justify-center !items-center !p-0",
      suppressKeyboardEvent: (params) => {
        if (params.event.key === 'Enter') {
          const leadName = params.data?.fullName || 'Lead';
          handleNotesClick(params.data.id, leadName);
          return true;
        }
        return false;
      },
      cellRenderer: (params: ICellRendererParams) => {
        const leadName = params.data?.fullName || 'Lead';
        
        return (
          <div
            onClick={() => handleNotesClick(params.data.id, leadName)}
            title="View/Edit Notes"
            className="flex justify-center items-center cursor-pointer"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="14" 
              height="14"               
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
              className="inline"
            >
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <path d="M12 7v6" />
              <path d="M9 10h6" />
            </svg>
          </div>
        );
      },
      // pinned: "left",

      ...colProps,
    };
  }

  static qualityRating(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    route: RouteFunction,
    qualityRatingOptions: QualityRating[],
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "quality.rating",
      filter: "agSetColumnFilter",
      minWidth: 58,
      maxWidth: 58,
      headerName: "",
      suppressColumnsToolPanel: true,
      cellStyle: { textAlign: "center" },
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({
          value: params.data?.quality,
          component: (
            <StatusIconLink route={route} data={params.data}></StatusIconLink>
          ),
        }),
      filterParams: {
        values: async (params: any) => {
          const filterModel = { ...params.api.getFilterModel() };
          delete filterModel["quality.rating"];

          const filterModelParam =
            convertFilterModelToApiQuery(filterModel) || "";
          const url = ENDPOINTS.UNIQUE_COLUMNS(
            entity,
            "quality_id",
            filterModelParam
          );

          try {
            const qualityIds = await requestFn({ path: url });

            if (Array.isArray(qualityIds)) {
              const displayValues = qualityIds
                .map(
                  (id) =>
                    qualityRatingOptions.find(({ id: qId }) => qId === id)
                      ?.rating
                )
                .filter(Boolean);
              params.success(displayValues);
            } else {
              params.success([]);
            }
          } catch (error) {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
      },
      ...colProps,
    };
  }

  static entityId(colProps: ColDef = {}): ColDef {
    //this column does not render or show in the column list. it only displays on export to denote the ID of the entity for each line
    return {
      field: "id",
      headerName: "id",
      hide: true,
      suppressColumnsToolPanel: true,
      ...colProps,
    };
  }

  static linkToEntity(route: RouteFunction, colProps: ColDef = {}): ColDef {
    return {
      field: "icon",
      filter: false,
      menuTabs: [],
      sortable: false,
      headerName: "",
      suppressColumnsToolPanel: true,
      maxWidth: 50, // REVIEW >> Not sure why this has to be set here, while defaultResponsiveOptions wasn't having an effect
      cellClass: "!flex !items-center",
      cellRenderer: (
        params: ICellRendererParams<
          | LeadResponse
          | AccountResponse
          | OpportunityResponse
          | ContactResponse,
          string
        >
      ) => (
        <Link
          href={route(params.data?.id)}
          className="text-center text-color-primary"
        >
          <Maximize2 />
        </Link>
      ),
      // pinned: "left", // Removed pinning to fix outline cutoff issue
      lockPosition: "left",
      ...colProps,
    };
  }

  static fullName(entity: SingularEntityType, colProps: ColDef = {}): ColDef {
    const route = routes[entity].base;
    return {
      field: "fullName",
      headerName:
        entity !== "contact"
          ? `${capitalize(entity)} Contact Name`
          : "Contact Name",
      filter: "agTextColumnFilter",
      cellRenderer: (params: ICellRendererParams<LeadResponse>) =>
        parseValue({
          value: params.value,
          component: (
            <Link href={route(params.data?.id)} className="text-color-primary">
              {params.value}
            </Link>
          ),
        }),
      ...colProps,
    };
  }

  static companyName(
    entity: SingularEntityType,
    colProps: ColDef = {}
  ): ColDef {
    const route = routes[entity].base;
    return {
      field: "companyName",
      headerName: `${capitalize(entity)} Company Name`,
      filter: "agTextColumnFilter",
      cellRenderer: (params: ICellRendererParams<LeadResponse>) =>
        parseValue({
          value: params.value,
          component: (
            <Link href={route(params.data?.id)} className="text-color-primary">
              {params.value}
            </Link>
          ),
        }),
      ...colProps,
    };
  }

  static status(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    data: LeadStatus[],
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "status.status",
      headerName: "Status",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          const filterModel = { ...params.api.getFilterModel() };
          delete filterModel["status.status"];

          const filterModelParam =
            convertFilterModelToApiQuery(filterModel) || "";
          const url = ENDPOINTS.UNIQUE_COLUMNS(
            entity,
            "status_id",
            filterModelParam
          );

          try {
            const statusIds = await requestFn({ path: url });

            if (Array.isArray(statusIds)) {
              const displayValues = statusIds
                .map((id) => data.find((s) => s.id === id)?.status)
                .filter((value) => value != null && value !== "");
              params.success(displayValues);
            } else {
              params.success([]);
            }
          } catch (error) {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
      },
      valueGetter: (params: ValueGetterParams) =>
        params.data?.status?.status ?? placeholder,
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({ value: params.value }),
      ...colProps,
    };
  }

  static source(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    data: MarketingSource[],
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "source.source",
      headerName: "Source",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          const filterModel = { ...params.api.getFilterModel() };
          delete filterModel["source.source"];

          const filterModelParam =
            convertFilterModelToApiQuery(filterModel) || "";
          const url = ENDPOINTS.UNIQUE_COLUMNS(
            entity,
            "source_id",
            filterModelParam
          );

          try {
            const sourceIds = await requestFn({ path: url });

            if (Array.isArray(sourceIds)) {
              const displayValues = sourceIds
                .map((id) => data.find((s) => s.id === id)?.source)
                .filter((label): label is string => !!label);
              params.success(displayValues);
            } else {
              params.success([]);
            }
          } catch (error) {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
      },
      valueGetter: (params: ValueGetterParams) =>
        params.data?.source?.source ?? placeholder,
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({ value: params.value }),
      ...colProps,
    };
  }

  static estimatedValue(colProps: ColDef = {}): ColDef {
    return {
      field: "estimatedValue",
      headerName: "Estimated Value",
      filter: "agNumberColumnFilter",
      cellRenderer: (
        params: ICellRendererParams<LeadResponse | OpportunityResponse, number>
      ) =>
        parseValue({
          value: params.value,
          component: (
            <FinancialText {...financialProps}>
              {params.value?.toString()}
            </FinancialText>
          ),
        }),
      ...colProps,
    };
  }

  static nextFollowUp(colProps: ColDef = {}): ColDef {
    return {
      field: "nextFollowUp",
      headerName: "Follow-Up",
      filter: "agDateColumnFilter",
      filterParams: {
        inRangeInclusive: true
      },
      valueFormatter: (params) => {
        return params.value
          ? new Date(params.value).toLocaleDateString()
          : placeholder;
      },
      cellRenderer: (
        params: ICellRendererParams<LeadResponse | OpportunityResponse, string>
      ) =>
        parseValue({
          value: params.value,
          component: (
            <DateText {...dateProps}>{params.value?.toString()}</DateText>
          ),
        }),
      filterValueGetter: (params: ValueGetterParams<LeadResponse>) =>
        new Date(params.data?.nextFollowUp),
      ...colProps,
    };
  }

  static updatedAt(colProps: ColDef = {}): ColDef {
    return {
      field: "updatedAt",
      headerName: "Last Activity",
      cellDataType: "dateString",
      filter: "agDateColumnFilter",
      filterParams: {
        inRangeInclusive: true
      },
      valueFormatter: (params) => {
        return params.value
          ? new Date(params.value).toLocaleString()
          : placeholder;
      },
      cellRenderer: (
        params: ICellRendererParams<
          | OpportunityResponse
          | LeadResponse
          | ContactResponse
          | AccountResponse
          | SiteResponse
        >
      ) =>
        parseValue({
          value: params.value,
          component: (
            <DateText {...dateProps}>{params.value.toString()}</DateText>
          ),
        }),
      ...colProps,
    };
  }

  static createdAt(colProps: ColDef = {}): ColDef {
    return {
      field: "createdAt",
      headerName: "Date Created",
      filter: "agDateColumnFilter",
      filterParams: {
        inRangeInclusive: true
      },
      valueFormatter: (params) => {
        return params.value
          ? new Date(params.value).toLocaleString()
          : placeholder;
      },
      cellRenderer: (
        params: ICellRendererParams<
          | OpportunityResponse
          | LeadResponse
          | ContactResponse
          | AccountResponse
          | SiteResponse,
          string
        >
      ) =>
        parseValue({
          value: params.value,
          component: (
            <DateText {...dateProps}>{params.value?.toString()}</DateText>
          ),
        }),
      filterValueGetter: (params: ValueGetterParams<LeadResponse>) =>
        new Date(params.data?.createdAt),
      ...colProps,
    };
  }

  static customerNumber(route: RouteFunction, colProps: ColDef = {}): ColDef {
    return {
      field: "erpId",
      headerName: "Customer #",
      cellRenderer: (params: ICellRendererParams<AccountResponse, string>) =>
        parseValue({
          value: params.value,
          component: (
            <Link
              prefetch={false}
              href={route(params.data?.id)}
              className="text-color-primary"
            >
              {params.value}
            </Link>
          ),
        }),
      filter: "agTextColumnFilter",
      ...colProps,
    };
  }

  static entityName(entity: SingularEntityType, colProps: ColDef = {}): ColDef {
    const route = routes[entity].base;
    return {
      field: "name",
      headerName: `${capitalize(entity)} Name`,
      cellRenderer: (
        params: ICellRendererParams<AccountResponse | CampaignResponse, string>
      ) =>
        parseValue({
          value: params.value,
          component: (
            <Link
              prefetch={false}
              href={route(params.data?.id)}
              className="text-color-primary"
            >
              {params.value}
            </Link>
          ),
        }),
      filter: "agTextColumnFilter",
      ...colProps,
    };
  }

  static accountManagementType(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    data: ManagementPreferenceResponse[],
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "accountManagementType.type",
      headerName: "Account Management",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          const filterModel = { ...params.api.getFilterModel() };
          delete filterModel["accountManagementType.type"];

          const filterModelParam =
            convertFilterModelToApiQuery(filterModel) || "";
          const url = ENDPOINTS.UNIQUE_COLUMNS(
            entity,
            "account_management_type_id",
            filterModelParam
          );

          try {
            const managementTypeIds = await requestFn({ path: url });

            if (Array.isArray(managementTypeIds)) {
              const displayValues = managementTypeIds
                .filter((value) => value != null && value !== "")
                .map((value) => {
                  const managementType = data.find(
                    (m) => m.id === (value as string)
                  );
                  return managementType ? managementType.type : value;
                })
                .filter((value) => value != null && value !== "");
              params.success(displayValues);
            } else {
              params.success([]);
            }
          } catch (error) {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
        valueFormatter: (
          params: ValueFormatterParams<
            AccountResponse | LeadResponse | ContactResponse
          >
        ) => {
          const managementType = data.find((m) => m.id === params.value);
          return managementType
            ? managementType.type
            : params.value || placeholder;
        },
      },
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({ value: params.value }),
      ...colProps,
    };
  }

  static primaryInterest(colProps: ColDef = {}): ColDef {
    return {
      field: "primaryInterest",
      headerName: "Primary Interest",
      filter: true,
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({ value: params.value }),
      ...colProps,
    };
  }

  static marketingGroup(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    data: MarketingGroup[],
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "marketingGroup.description",
      headerName: "Marketing Group",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          const filterModel = { ...params.api.getFilterModel() };
          delete filterModel["marketingGroup.description"];

          const filterModelParam =
            convertFilterModelToApiQuery(filterModel) || "";
          const url = ENDPOINTS.UNIQUE_COLUMNS(
            entity,
            "marketing_group_id",
            filterModelParam
          );

          try {
            const marketingGroupIds = await requestFn({ path: url });

            if (Array.isArray(marketingGroupIds)) {
              const displayValues = marketingGroupIds
                .filter((value) => value != null && value !== "")
                .map((value) => {
                  const marketingGroup = data.find(
                    (m) => m.id === (value as string)
                  );
                  return marketingGroup ? marketingGroup.description : value;
                })
                .filter((value) => value != null && value !== "");
              params.success(displayValues);
            } else {
              params.success([]);
            }
          } catch (error) {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
        valueFormatter: (
          params: ValueFormatterParams<
            AccountResponse | LeadResponse | ContactResponse
          >
        ) => {
          const marketingGroup = data.find((m) => m.id === params.value);
          return marketingGroup
            ? marketingGroup.description
            : params.value || placeholder;
        },
      },
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({ value: params.value }),
      ...colProps,
    };
  }

  static accountPortfolioManager(colProps: ColDef = {}): ColDef {
    return {
      field: "accountPortfolioManager.fullName",
      headerName: "Account Manager",
      filter: "agTextColumnFilter",
      cellRenderer: (
        params: ICellRendererParams<OpportunityResponse | AccountResponse>
      ) =>
        parseValue({
          value: params.value,
          component: (
            <Link
              className="text-color-primary"
              href={routes.contact.base(
                params.data?.accountPortfolioManager?.id
              )}
              prefetch={false}
            >
              {params.value}
            </Link>
          ),
        }),
      ...colProps,
    };
  }

  static businessDevelopmentManager(colProps: ColDef = {}): ColDef {
    return {
      field: "businessDevelopmentManager.fullName",
      headerName: "Current Assignee",
      filter: "agTextColumnFilter",
      cellRenderer: (
        params: ICellRendererParams<OpportunityResponse | AccountResponse>
      ) =>
        parseValue({
          value: params.value,
          component: (
            <Link
              className="text-color-primary"
              href={routes.contact.base(
                params.data?.businessDevelopmentManager?.id
              )}
              prefetch={false}
            >
              {params.value}
            </Link>
          ),
        }),
      ...colProps,
    };
  }

  static totalOpportunities(colProps: ColDef = {}): ColDef {
    return {
      field: "totalOpportunities",
      headerName: "Opportunities",
      filter: "agNumberColumnFilter",
      cellRenderer: (params: ICellRendererParams) =>
        parseValue({ value: params.value }),
      ...colProps,
    };
  }

  static totalEstimatedRevenue(colProps: ColDef = {}): ColDef {
    return {
      field: "totalEstimatedRevenue",
      headerName: "Opportunity $",
      filter: "agNumberColumnFilter",
      cellRenderer: (params: ICellRendererParams<AccountResponse, number>) =>
        parseValue({
          value: params.value,
          component: (
            <FinancialText {...financialProps}>
              {params.value?.toString()}
            </FinancialText>
          ),
        }),
      ...colProps,
    };
  }

  static previousYearSales(colProps: ColDef = {}): ColDef {
    return {
      field: "previousYearSalesToDate",
      headerName: `${prevYear} Sales`,
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        return params.value
          ? parseFloat(params.value).toFixed(2).toString()
          : placeholder;
      },
      cellRenderer: (params: ICellRendererParams<AccountResponse, number>) =>
        parseValue({
          value: params.value,
          component: (
            <FinancialText {...financialProps}>
              {params.value?.toString()}
            </FinancialText>
          ),
        }),
      ...colProps,
    };
  }

  static currentYearSales(colProps: ColDef = {}): ColDef {
    return {
      field: "currentYearSalesToDate",
      headerName: `${currentYear} Sales YTD`,
      filter: "agNumberColumnFilter",
      valueFormatter: (params) => {
        return params.value
          ? parseFloat(params.value).toFixed(2).toString()
          : placeholder;
      },
      cellRenderer: (params: ICellRendererParams<AccountResponse, number>) =>
        parseValue({
          value: params.value,
          component: (
            <FinancialText {...financialProps}>
              {params.value?.toString()}
            </FinancialText>
          ),
        }),
      ...colProps,
    };
  }

  static campaign(colProps: ColDef = {}): ColDef {
    return {
      field: "campaign.name",
      headerName: "Campaign",
      filter: "agTextColumnFilter",
      minWidth: 200,
      cellRenderer: (
        params: ICellRendererParams<
          LeadResponse | OpportunityResponse,
          CampaignResponse
        >
      ) =>
        parseValue({
          value: params.value,
          component: (
            <Link
              key={params.data.campaign?.id}
              className="text-color-primary"
              href={routes.campaign.base(params.data.campaign?.id)}
              prefetch={false}
            >
              {params.data.campaign?.name}
            </Link>
          ),
        }),
      ...colProps,
    };
  }

  static Account = {
    status(
      entity: ENTITY,
      requestFn: <APIResponseType>({
        url,
        path,
        auth,
        refresh,
        ...options
      }?: FetchOptions) => Promise<APIResponseType>,
      colProps: ColDef = {}
    ): ColDef {
      return {
        field: "accountStatus",
        valueFormatter: (params: ValueFormatterParams<AccountResponse>) =>
          params.value ? "Active" : "Inactive",
        filter: "agSetColumnFilter",
        filterParams: {
          valueFormatter: (params: ValueFormatterParams<AccountResponse>) =>
            params.value ? "Active" : "Inactive",
          values: async (params: any) => {
            const filterModel = { ...params.api.getFilterModel() };
            delete filterModel["accountStatus"];

            const filterModelParam =
              convertFilterModelToApiQuery(filterModel) || "";
            const url = ENDPOINTS.UNIQUE_COLUMNS(
              entity,
              "account_status",
              filterModelParam
            );

            try {
              const statusValues = await requestFn({ path: url });

              if (Array.isArray(statusValues)) {
                // Keep the original values for proper filtering
                const displayValues = statusValues.filter(
                  (value) => value !== null
                );
                params.success(displayValues);
              } else {
                params.success([]);
              }
            } catch (error) {
              params.success([]);
            }
          },
          refreshValuesOnOpen: true,
        },
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({
            value: params.value,
            component: <>{params.value ? "Active" : "Inactive"}</>,
          }),
        ...colProps,
      };
    },

    Site: {
      id(colProps: ColDef = {}): ColDef {
        return {
          field: "erpId",
          headerName: "FSG Site ID",
          filter: "agTextColumnFilter",
          cellRenderer: (params: ICellRendererParams<SiteResponse>) =>
            parseValue({
              value: params.value,
              component: (
                <Link
                  prefetch={false}
                  href={`/sites/${params.data?.id}/general`}
                  className="text-color-primary"
                >
                  {params.value}
                </Link>
              ),
            }),
          ...colProps,
        };
      },

      storeNumber(colProps: ColDef = {}): ColDef {
        return {
          field: "customerSiteId",
          headerName: "Customer Store Number",
          filter: "agTextColumnFilter",
          cellRenderer: (params: ICellRendererParams<SiteResponse>) =>
            parseValue({
              value: params.value,
              component: (
                <Link
                  prefetch={false}
                  href={`/sites/${params.data?.id}/general`}
                  className="text-color-primary"
                >
                  {params.value}
                </Link>
              ),
            }),
          ...colProps,
        };
      },

      select(
        {
          fields,
          append,
          remove,
        }: {
          fields: any[];
          append: (site?: SiteOption) => void;
          remove: (index?: number) => void;
        },
        colProps: ColDef = {}
      ): ColDef {
        return {
          field: "select",
          headerName: "",
          maxWidth: 50,
          suppressMovable: true,
          suppressColumnsToolPanel: true,
          sortable: false,
          checkboxSelection: true,
          suppressHeaderMenuButton: true,
          // valueGetter: (params: ValueGetterParams<SiteResponse>) => {
          //   const checkedField = fields.find((site: any) => site.value === params.data?.id)
          //   return checkedField ? 1 : 0
          // },
          // cellRenderer: (params: ICellRendererParams<SiteResponse>) => {
          //   const checkedField = fields.find((site: any) => site.value === params.data.id)
          //   const isChecked = checkedField !== undefined
          //   function handleOnChange(site: SiteResponse) {
          //     if (!isChecked) {
          //       append({
          //         label: site.name,
          //         value: site.id,
          //         site: site,
          //       })
          //     } else {
          //       remove(fields.indexOf(checkedField))
          //     }
          //   }

          //   return (
          //     <input
          //       className="!inline"
          //       type="checkbox"
          //       checked={isChecked}
          //       onChange={() => {
          //         handleOnChange(params.data)
          //       }}
          //     />
          //   )
          // },

          ...colProps,
        };
      },

      terms(colProps: ColDef = {}): ColDef {
        return {
          field: "netTerms",
          headerName: "Terms",
          ...colProps,
        };
      },
    },
  };

  static Opportunity = {
    probability(colProps: ColDef = {}): ColDef {
      return {
        field: "probability",
        headerName: "Probability",
        filter: true,
        hide: true,
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },

    accountName(colProps: ColDef = {}): ColDef {
      return {
        field: "account.name",
        headerName: `Customer`,
        cellRenderer: (
          params: ICellRendererParams<OpportunityResponse, string>
        ) =>
          parseValue({
            value: params.value,
            component: (
              <Link
                prefetch={false}
                href={routes["account"].base(params.data?.account.id)}
                className="text-color-primary"
              >
                {params.value}
              </Link>
            ),
          }),
        filter: "agTextColumnFilter",
        ...colProps,
      };
    },

    description(colProps: ColDef = {}): ColDef {
      return {
        field: "opportunityDescription",
        headerName: "Opportunity Description",
        filter: true,
        hide: true,
        resizable: true,
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },
    estCloseDate(colProps: ColDef = {}): ColDef {
      return {
        field: "estCloseDate",
        headerName: "Estimated Close Date",
        valueFormatter: (params) => {
          return params.value
            ? new Date(params.value).toLocaleDateString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<OpportunityResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <DateText {...dateProps}>{params.value?.toString()}</DateText>
            ),
          }),
        filter: "agDateColumnFilter",
        filterParams: {
          inRangeInclusive: true
        },
        ...colProps,
      };
    },
    bidDueDate(colProps: ColDef = {}): ColDef {
      return {
        field: "bidDueDate",
        headerName: "Due Date",
        cellDataType: "dateString",
        filter: "agDateColumnFilter",
        filterParams: {
          inRangeInclusive: true
        },
        valueFormatter: (params) => {
          return params.value
            ? new Date(params.value).toLocaleDateString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<OpportunityResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <DateText {...dateProps}>{params.value?.toString()}</DateText>
            ),
          }),
        ...colProps,
      };
    },

    budget(colProps: ColDef = {}): ColDef {
      return {
        field: "budget",
        headerName: "Budget",
        cellRenderer: (params: ICellRendererParams<OpportunityResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <FinancialText {...financialProps}>
                {params.value.toString()}
              </FinancialText>
            ),
          }),
        filter: "agNumberColumnFilter",
        ...colProps,
      };
    },

    primaryStakeholder(colProps: ColDef = {}): ColDef {
      return {
        field: "primaryStakeholder.fullName",
        headerName: "Primary Customer Stakeholder",
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams<OpportunityResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <Link
                prefetch={false}
                href={`/contacts/${params.data?.primaryStakeholder?.id}/basic-information`}
                className="text-color-primary"
              >
                {params.value}
              </Link>
            ),
          }),
        ...colProps,
      };
    },

    team(colProps: ColDef = {}): ColDef {
      return {
        field: "estimators.fullName",
        headerName: "Team",
        filter: "agTextColumnFilter",
        valueGetter: (params: ValueGetterParams<OpportunityResponse>) => {
          return params.data?.estimators;
        },
        valueFormatter: (params) => {
          return params.data?.estimators.length > 0
            ? params.value?.map((contact: ContactResponse) => contact.fullName)
            : placeholder;
        },
        minWidth: 200,
        cellRenderer: (
          params: ICellRendererParams<
            OpportunityResponse,
            OpportunityResponse["estimators"]
          >
        ) =>
          parseValue({
            value: params.value,
            component: (
              <CellListWithPopover>
                {params.value?.map((contact) => (
                  <Link
                    key={contact.id}
                    className="text-color-primary"
                    href={routes.contact.base(contact.id)}
                    prefetch={false}
                  >
                    {contact.fullName}
                  </Link>
                ))}
              </CellListWithPopover>
            ),
          }),
        ...colProps,
      };
    },

    stage(
      entity: ENTITY,
      requestFn: <APIResponseType>({
        url,
        path,
        auth,
        refresh,
        ...options
      }?: FetchOptions) => Promise<APIResponseType>,
      ENUMS: OpportunitiesEnums,
      colProps: ColDef = {}
    ): ColDef {
      return {
        field: "stage",
        headerName: "Stage",
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params: any) => {
            const filterModel = { ...params.api.getFilterModel() };
            delete filterModel["stage"];

            const filterModelParam =
              convertFilterModelToApiQuery(filterModel) || "";
            const url = ENDPOINTS.UNIQUE_COLUMNS(
              entity,
              "stage",
              filterModelParam
            );

            try {
              const stageValues = await requestFn({ path: url });

              if (Array.isArray(stageValues)) {
                const displayValues = stageValues
                  .filter((value) => value != null && value !== "")
                  .map((value) => value);
                params.success(displayValues);
              } else {
                params.success([]);
              }
            } catch (error) {
              params.success([]);
            }
          },
          refreshValuesOnOpen: true,
          cellRenderer: (params: any) => {
            return normalizeENUM(params.value);
          },
        },
        valueGetter: (params: ValueGetterParams) => {
          // Handle different possible data structures for stage
          if (params.data?.stage?.stage) {
            return params.data.stage.stage;
          } else if (params.data?.stage) {
            return params.data.stage;
          }
          return placeholder;
        },
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({
            value: params.value,
            component: <>{normalizeENUM(params.value ?? "")}</>,
          }),
        ...colProps,
      };
    },

    services(services: FSGServices[], colProps: ColDef = {}): ColDef {
      return {
        field: "services",
        headerName: "FSG Services",
        cellRenderer: (
          params: ICellRendererParams<
            OpportunityResponse,
            OpportunityResponse["services"]
          >
        ) =>
          parseValue({
            value: params.value,
            component: (
              <CellListWithPopover className="flex items-center">
                {params.value?.map((service: FSGServices, i) => (
                  <p key={`${service}-${i}`}>
                    {`${service}${i === params.value.length - 1 ? "" : ","}`}{" "}
                  </p>
                ))}
              </CellListWithPopover>
            ),
          }),
        valueGetter: (params: any) =>
          params.data?.services?.map(
            (service: FSGServices) => service.serviceType
          ),
        filter: "agSetColumnFilter",
        filterParams: {
          values: services.map((service: FSGServices) => service.id),
          valueGetter: (params: any) =>
            params.data.services?.map((service: FSGServices) => service.id),
          cellRenderer: (params: any) => {
            // Display serviceType in the filter drop-down
            const service = services.find((s) => s.id === params.value);
            return service ? service.serviceType : params.value;
          },
        },
        ...colProps,
      };
    },
  };

  static Campaign = {
    type(
      entity: ENTITY,
      requestFn: <APIResponseType>({
        url,
        path,
        auth,
        refresh,
        ...options
      }?: FetchOptions) => Promise<APIResponseType>,
      enums: CampaignEnums,
      colProps: ColDef = {}
    ): ColDef {
      return {
        field: "type",
        headerName: "Type",
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params: any) => {
            const filterModel = { ...params.api.getFilterModel() };
            delete filterModel["type"];

            const filterModelParam =
              convertFilterModelToApiQuery(filterModel) || "";
            const url = ENDPOINTS.UNIQUE_COLUMNS(entity, "type", filterModelParam);

            try {
              const typeValues = await requestFn({ path: url });

              if (Array.isArray(typeValues)) {
                const displayValues = typeValues
                  .filter((value) => value != null && value !== "")
                  .map((value) => value);
                params.success(displayValues);
              } else {
                params.success([]);
              }
            } catch (error) {
              params.success([]);
            }
          },
          refreshValuesOnOpen: true,
          cellRenderer: (params: any) => {
            return formatCampaignFilterSet(params.value ?? "");
          },
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return formatCampaignFilterSet(params.value ?? "");
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse, string>) =>
          parseValue({
            value: params.value,
            component: <>{formatCampaignFilterSet(params.value ?? "")}</>,
          }),
        ...colProps,
      };
    },
    focus(
      entity: ENTITY,
      requestFn: <APIResponseType>({
        url,
        path,
        auth,
        refresh,
        ...options
      }?: FetchOptions) => Promise<APIResponseType>,
      enums: CampaignEnums,
      colProps: ColDef = {}
    ): ColDef {
      return {
        field: "focus",
        headerName: "Focus",
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params: any) => {
            const filterModel = { ...params.api.getFilterModel() };
            delete filterModel["focus"];

            const filterModelParam =
              convertFilterModelToApiQuery(filterModel) || "";
            const url = ENDPOINTS.UNIQUE_COLUMNS(
              entity,
              "focus",
              filterModelParam
            );

            try {
              const focusValues = await requestFn({ path: url });

              if (Array.isArray(focusValues)) {
                const displayValues = focusValues
                  .filter((value) => value != null && value !== "")
                  .map((value) => value);
                params.success(displayValues);
              } else {
                params.success([]);
              }
            } catch (error) {
              params.success([]);
            }
          },
          refreshValuesOnOpen: true,
          cellRenderer: (params: any) => {
            return formatCampaignFilterSet(params.value ?? "");
          },
        },
        valueFormatter: (params: ValueFormatterParams) => {
          return formatCampaignFilterSet(params.value ?? "");
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse, string>) =>
          parseValue({
            value: params.value,
            component: <>{formatCampaignFilterSet(params.value ?? "")}</>,
          }),
        ...colProps,
      };
    },
    plannedStart(colProps: ColDef = {}): ColDef {
      return {
        field: "plannedStart",
        headerName: "Planned Start",
        filter: "agDateColumnFilter",
        filterParams: {
          inRangeInclusive: true
        },
        valueFormatter: (params) => {
          return params.value
            ? new Date(params.value).toLocaleDateString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse, string>) =>
          parseValue({
            value: params.value,
            component: (
              <DateText {...dateProps}>
                {new Date(params.value).toString()}
              </DateText>
            ),
          }),
        ...colProps,
      };
    },

    plannedEnd(colProps: ColDef = {}): ColDef {
      return {
        field: "plannedEnd",
        headerName: "Planned End",
        filter: "agDateColumnFilter",
        filterParams: {
          inRangeInclusive: true
        },
        valueFormatter: (params) => {
          return params.value
            ? new Date(params.value).toLocaleDateString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse, string>) =>
          parseValue({
            value: params.value,
            component: (
              <DateText {...dateProps}>
                {new Date(params.value).toString()}
              </DateText>
            ),
          }),
        ...colProps,
      };
    },
    actualCost(colProps: ColDef = {}): ColDef {
      return {
        field: "actualCost",
        headerName: "Actual Cost",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          return params.value
            ? parseFloat(params.value).toFixed(2).toString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <FinancialText {...financialProps}>
                {params.value?.toString()}
              </FinancialText>
            ),
          }),
        ...colProps,
      };
    },
    actualRevenue(colProps: ColDef = {}): ColDef {
      return {
        field: "actualRevenue",
        headerName: "Actual Revenue",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          return params.value
            ? parseFloat(params.value).toFixed(2).toString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <FinancialText {...financialProps}>
                {params.value?.toString()}
              </FinancialText>
            ),
          }),
        ...colProps,
      };
    },
    actualROI(colProps: ColDef = {}): ColDef {
      return {
        field: "actualRevenueROI",
        headerName: "ROI on Actual Revenue",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          return params.value
            ? parseFloat(params.value).toFixed(2).toString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <FinancialText {...financialProps}>
                {params.value?.toString()}
              </FinancialText>
            ),
          }),
        ...colProps,
      };
    },
    grossROI(colProps: ColDef = {}): ColDef {
      return {
        field: "grossProfitROI",
        headerName: "ROI on Actual Gross Profit",
        filter: "agNumberColumnFilter",
        valueFormatter: (params) => {
          return params.value
            ? parseFloat(params.value).toFixed(2).toString()
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<CampaignResponse>) =>
          parseValue({
            value: params.value,
            component: (
              <FinancialText {...financialProps}>
                {params.value?.toString()}
              </FinancialText>
            ),
          }),
        ...colProps,
      };
    },
    totalLeads(colProps: ColDef = {}): ColDef {
      return {
        field: "totalLeads",
        headerName: "Lead Count",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },

    Expense: {
      link(
        route: RouteFunction,
        campaignId: string,
        colProps: ColDef = {}
      ): ColDef {
        return {
          field: "id",
          filter: false,
          menuTabs: [],
          sortable: false,
          headerName: "",
          suppressColumnsToolPanel: true,
          maxWidth: 50, // REVIEW >> Not sure why this has to be set here, while defaultResponsiveOptions wasn't having an effect
          cellClass: "!flex !items-center",
          cellRenderer: (
            params: ICellRendererParams<ExpenseResponse, string>
          ) => (
            <Link
              href={route(campaignId, params.value)}
              className="text-center text-color-primary"
            >
              <Maximize2 />
            </Link>
          ),
          ...colProps,
        };
      },
      name(
        route: RouteFunction,
        campaignId: string,
        colProps: ColDef = {}
      ): ColDef {
        return {
          field: "name",
          headerName: "Expense #",
          filter: "agNumberColumnFilter",
          cellRenderer: (params: ICellRendererParams<ExpenseResponse>) =>
            parseValue({
              value: params.value,
              component: (
                <Link
                  href={route(campaignId, params.data?.id)}
                  className="text-color-primary"
                >
                  {params.value}
                </Link>
              ),
            }),
          ...colProps,
        };
      },

      paymentDate(colProps: ColDef = {}): ColDef {
        return {
          field: "paymentDate",
          headerName: "Payment Date",
          filter: "agDateColumnFilter",
          filterParams: {
            inRangeInclusive: true
          },
          cellRenderer: (
            params: ICellRendererParams<ExpenseResponse, string>
          ) =>
            parseValue({
              value: params.value,
              component: (
                <DateText {...dateProps}>
                  {new Date(params.value).toString()}
                </DateText>
              ),
            }),
          ...colProps,
        };
      },
      payee(colProps: ColDef = {}): ColDef {
        return {
          field: "payee",
          headerName: "Payee",
          filter: "agTextColumnFilter",
          cellRenderer: (params: ICellRendererParams) =>
            parseValue({ value: params.value }),
          ...colProps,
        };
      },
      description(colProps: ColDef = {}): ColDef {
        return {
          field: "description",
          headerName: "Description",
          filter: "agTextColumnFilter",
          cellRenderer: (
            params: ICellRendererParams<ExpenseResponse, string>
          ) => parseValue({ value: params.value }),
          ...colProps,
        };
      },
      costCode(colProps: ColDef = {}): ColDef {
        return {
          field: "costCode.description",
          headerName: "Cost Code",
          filter: "agSetColumnFilter",
          cellRenderer: (
            params: ICellRendererParams<ExpenseResponse, string>
          ) =>
            parseValue({
              value: params.data?.costCode?.code,
              component: (
                <>{`${params.data?.costCode?.code} - ${params.data?.costCode?.description}`}</>
              ),
            }),
          ...colProps,
        };
      },
      owner(colProps: ColDef = {}): ColDef {
        return {
          field: "owner",
          headerName: "Owner",
          filter: "agTextColumnFilter",
          cellRenderer: (
            params: ICellRendererParams<ExpenseResponse, ContactResponse>
          ) =>
            parseValue({
              value: params.value,
              component: (
                <Link
                  href={routes.contact.basicInformation(params.value.id)}
                  className="text-color-primary"
                >
                  {params.value.fullName}
                </Link>
              ),
            }),
          ...colProps,
        };
      },
      total(colProps: ColDef = {}): ColDef {
        return {
          field: "total",
          headerName: "Total",
          filter: "agNumberColumnFilter",
          cellRenderer: (
            params: ICellRendererParams<ExpenseResponse, number>
          ) =>
            parseValue({
              value: params.value,
              component: (
                <FinancialText {...financialProps}>
                  {params.value?.toString()}
                </FinancialText>
              ),
            }),
          ...colProps,
        };
      },
    },
  };

  static Activity = {
    type(colProps: ColDef = {}): ColDef {
      return {
        field: "activityType",
        headerName: "Activity Type",
        valueGetter: (params) => normalizeENUM(params.data?.activityType),
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({
            value: params.value,
            component: <>{normalizeENUM(params.data?.activityType ?? "")}</>,
          }),
        ...colProps,
      };
    },
    typeIcon(colProps: ColDef = {}): ColDef {
      const iconLookup = [
        { activityType: "PHONE_CALL", icon: () => <PhoneCall /> },
        { activityType: "EMAIL", icon: () => <Mail /> },
        { activityType: "MEETING", icon: () => <Calendar /> },
        // { activityType: 'Task Creation', icon: () => <CheckSquare /> },
        // { activityType: 'Task Completion', icon: () => <CheckSquare /> },
        { activityType: "STATUS_CHANGE", icon: () => <FileTextCheck /> },
        { activityType: "VIEW_ONLY", icon: () => <Eye /> },
        { activityType: "EDIT", icon: () => <Edit3 /> },
      ];

      return {
        field: "icon",
        filter: false,
        menuTabs: [],
        sortable: false,
        headerName: "",
        suppressColumnsToolPanel: true,
        maxWidth: 50, // REVIEW >> Not sure why this has to be set here, while defaultResponsiveOptions wasn't having an effect
        cellClass: "!flex !items-center",
        cellRenderer: (params: ICellRendererParams) => {
          const matchedIcon = iconLookup.find(
            (obj) => obj.activityType === params.data?.activityType
          );
          return matchedIcon ? matchedIcon.icon() : null;
        },
        ...colProps,
      };
    },
    by(colProps: ColDef = {}): ColDef {
      return {
        field: "user.email",
        headerName: "Activity By",
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },
    date(colProps: ColDef = {}): ColDef {
      return {
        headerName: "Date",
        filter: "agDateColumnFilter",
        filterParams: {
          inRangeInclusive: true
        },
        valueGetter: (params) => {
          const date = new Date(params.data?.recordedAt);
          return date.setHours(0, 0, 0, 0);
        },
        sort: "desc",
        cellRenderer: (params: ICellRendererParams<ActivityResponse, string>) =>
          parseValue({
            value: params.data?.recordedAt,
            component: (
              <DateText {...dateProps} hideTime={false}>
                {params.data?.recordedAt?.toString()}
              </DateText>
            ),
          }),
        ...colProps,
      };
    },
    details(colProps: ColDef = {}): ColDef {
      return {
        field: "notes.content",
        headerName: "Details",
        valueFormatter: (params) =>
          params.data?.activityType === "VIEW_ONLY"
            ? `Daily views: ${params.data?.notes.length}`
            : params.data?.notes.length > 0
              ? params.data?.notes[0].content
              : placeholder,
        ...colProps,
      };
    },
  };

  static Contact = {
    companyName(colProps: ColDef = {}): ColDef {
      return {
        field: "currentRole.accountName",
        headerName: "Company Name",
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams<ContactResponse, string>) =>
          parseValue({
            value: params.data?.currentRole?.accountName,
            component: (
              <Link
                prefetch={false}
                href={`accounts/${params.data?.currentRole?.accountId}/general/basic-information`}
                className="text-color-primary"
              >
                {params.data?.currentRole?.accountName}
              </Link>
            ),
          }),
        ...colProps,
      };
    },
    title(colProps: ColDef = {}): ColDef {
      return {
        field: "currentRole.title",
        headerName: "Title",
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },
    jobFunctions(colProps: ColDef = {}): ColDef {
      return {
        field: "currentRole.jobFunctions",
        headerName: "Job Functions",
        filter: "agTextColumnFilter",
        cellRenderer: (
          params: ICellRendererParams<ContactResponse, JobFunction[]>
        ) => {
          return params.value.length > 0
            ? params.value.map((j) => j.function).join(", ")
            : placeholder;
        },
        ...colProps,
      };
    },
  };

  static salesType(
    entity: ENTITY,
    requestFn: <APIResponseType>({
      url,
      path,
      auth,
      refresh,
      ...options
    }?: FetchOptions) => Promise<APIResponseType>,
    ENUMS: OpportunitiesEnums,
    colProps: ColDef = {}
  ): ColDef {
    return {
      field: "salesType",
      headerName: "Sales Type",
      filter: "agSetColumnFilter",
      filterParams: {
        values: async (params: any) => {
          const filterModel = { ...params.api.getFilterModel() };
          delete filterModel["salesType"];

          const filterModelParam =
            convertFilterModelToApiQuery(filterModel) || "";
          const url = ENDPOINTS.UNIQUE_COLUMNS(
            entity,
            "sales_type",
            filterModelParam
          );

          try {
            const salesTypeValues = await requestFn({ path: url });

            if (Array.isArray(salesTypeValues)) {
              const displayValues = salesTypeValues.filter(
                (value) => value != null && value !== ""
              );
              params.success(displayValues);
            } else {
              params.success([]);
            }
          } catch (error) {
            params.success([]);
          }
        },
        refreshValuesOnOpen: true,
        cellRenderer: (params: any) => {
          return normalizeENUM(params.value);
        },
      },
      cellRenderer: (params: ICellRendererParams<OpportunityResponse>) =>
        parseValue({
          value: params.value,
          component: <>{normalizeENUM(params.value ?? "")}</>,
        }),
      ...colProps,
    };
  }

  static primaryRep(colProps: ColDef = {}): ColDef {
    return {
      field: "primaryRep.fullName",
      headerName: "FSG Owner",
      filter: "agTextColumnFilter",
      cellRenderer: (params: ICellRendererParams<LeadResponse, string>) =>
        parseValue({
          value: params.value,
          component: (
            <Link
              href={routes.contact.base(params.data?.primaryRep?.id)}
              className="text-color-primary"
            >
              {params.value}
            </Link>
          ),
        }),
      ...colProps,
    };
  }

  static PrimaryPhone = {
    number(colProps: ColDef = {}): ColDef {
      return {
        field: "primaryPhone.number",
        headerName: "Phone Number",
        filter: "agTextColumnFilter",
        suppressKeyboardEvent: (params) => {
          if (params.event.key === 'Enter') {
            const phoneNumber = params.data?.primaryPhone?.number;
            if (phoneNumber && phoneNumber.length > 0) {
              // Open phone call link in same window
              window.location.href = `tel:${phoneNumber}`;
              return true;
            }
          }
          return false;
        },
        valueGetter: (params) => {
          let phoneNumber = params.data?.primaryPhone?.number;
          if (phoneNumber?.length > 0) {
            const number = formatPhoneNumber(phoneNumber);
            return number?.formatted ?? "";
          }
          return "";
        },
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },
  };

  static PrimaryEmail = {
    address(colProps: ColDef = {}): ColDef {
      return {
        field: "primaryEmail.address",
        headerName: "Email",
        filter: "agTextColumnFilter",
        suppressKeyboardEvent: (params) => {
          if (params.event.key === 'Enter') {
            const email = params.data?.primaryEmail?.address;
            if (email && email.length > 0) {
              // Open email link in same window
              window.location.href = `mailto:${email}`;
              return true;
            }
          }
          return false;
        },
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.value }),
        ...colProps,
      };
    },
  };

  static PrimaryAddress = {
    city(colProps: ColDef = {}): ColDef {
      return {
        field: "primaryAddress.city",
        headerName: "City",
        // NOTE: accounts should always have at least one address because it is a required field on creation
        cellRenderer: (
          params: ICellRendererParams<
            AccountResponse | LeadResponse | ContactResponse,
            string
          >
        ) => parseValue({ value: params.data?.primaryAddress?.city }),
        valueFormatter: (params) =>
          params.data?.primaryAddress
            ? params.data?.primaryAddress.city
            : placeholder,
        filter: "agTextColumnFilter",
        ...colProps,
      };
    },
    state(
      entity: ENTITY,
      requestFn: <APIResponseType>({
        url,
        path,
        auth,
        refresh,
        ...options
      }?: FetchOptions) => Promise<APIResponseType>,
      states: StateResponse[],
      provinces?: StateResponse[],
      colProps: ColDef = {}
    ): ColDef {
      if (provinces === undefined) {
        provinces = [];
      }
      return {
        field: "primaryAddress.state.name",
        headerName: "State",
        cellRenderer: (
          params: ICellRendererParams<
            AccountResponse | LeadResponse | ContactResponse,
            string
          >
        ) => parseValue({ value: params.data?.primaryAddress?.state }),
        valueFormatter: (params) =>
          params.data?.primaryAddress
            ? params.data?.primaryAddress.state
            : placeholder,
        filter: "agSetColumnFilter",
        filterParams: {
          values: async (params: any) => {
            const filterModel = { ...params.api.getFilterModel() };
            delete filterModel["primaryAddress.state.name"];

            const filterModelParam =
              convertFilterModelToApiQuery(filterModel) || "";
            const url = ENDPOINTS.UNIQUE_COLUMNS(
              entity,
              "state",
              filterModelParam
            );

            try {
              const stateIds = await requestFn({ path: url });

              if (Array.isArray(stateIds)) {
                const displayValues = stateIds
                  .map(
                    (id) =>
                      states.find((s) => s.id === id)?.name ||
                      provinces.find((p) => p.id === id)?.name ||
                      id
                  )
                  .filter(Boolean);
                params.success(displayValues);
              } else {
                params.success([]);
              }
            } catch (error) {
              params.success([]);
            }
          },
          refreshValuesOnOpen: true,
          comparator: noSortComparator,
        },
        ...colProps,
      };
    },
    street(colProps: ColDef = {}): ColDef {
      return {
        field: "primaryAddress.line1",
        headerName: "Street Address",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          return params.data?.primaryAddress
            ? params.data?.primaryAddress.line1
            : placeholder;
        },
        cellRenderer: (
          params: ICellRendererParams<
            AccountResponse | LeadResponse | ContactResponse,
            string
          >
        ) => parseValue({ value: params.data?.primaryAddress?.line1 }),
        ...colProps,
      };
    },
  };

  static BillingAddress = {
    city(colProps: ColDef = {}): ColDef {
      return {
        field: "billingAddress.city",
        headerName: "Billing City",
        // NOTE: accounts should always have at least one address because it is a required field on creation
        cellRenderer: (params: ICellRendererParams<SiteResponse, string>) =>
          parseValue({ value: params.data?.billingAddress?.city }),
        valueFormatter: (params) =>
          params.data?.billingAddress
            ? params.data?.billingAddress.city
            : placeholder,
        filter: "agTextColumnFilter",
        ...colProps,
      };
    },
    state(
      states: StateResponse[],
      provinces?: StateResponse[],
      colProps: ColDef = {}
    ): ColDef {
      if (provinces === undefined) {
        provinces = [];
      }
      const stateValues = [
        ...states.map((state) => state.name),
        ...provinces.map((state) => state.name),
      ];
      return {
        field: "billingAddress.state.name",
        headerName: "Billing State/Province/Territory",
        cellRenderer: (params: ICellRendererParams<SiteResponse, string>) =>
          parseValue({ value: params.data?.billingAddress?.state }),
        valueFormatter: (params) =>
          params.data?.billingAddress
            ? params.data?.billingAddress.state
            : placeholder,
        filter: "agSetColumnFilter",
        filterParams: {
          values: stateValues,
          comparator: noSortComparator,
        },
        ...colProps,
      };
    },
    street(colProps: ColDef = {}): ColDef {
      return {
        field: "billingAddress.line1",
        headerName: "Billing Street Address",
        filter: "agTextColumnFilter",
        valueFormatter: (params) => {
          return params.data?.billingAddress
            ? params.data?.billingAddress.line1
            : placeholder;
        },
        cellRenderer: (params: ICellRendererParams<SiteResponse, string>) =>
          parseValue({ value: params.data?.billingAddress?.line1 }),
        ...colProps,
      };
    },
  };

  static MarketingSummary = {
    linkToEntity(colProps: ColDef = {}): ColDef {
      return {
        field: "icon",
        filter: false,
        menuTabs: [],
        sortable: false,
        headerName: "",
        suppressColumnsToolPanel: true,
        maxWidth: 50, // REVIEW >> Not sure why this has to be set here, while defaultResponsiveOptions wasn't having an effect
        cellClass: "!flex !items-center",
        cellRenderer: (
          params: ICellRendererParams<
            LeadResponse | OpportunityResponse,
            string
          >
        ) => {
          const route =
            (params.data as any).type === "lead"
              ? routes.lead.base
              : routes.opportunity.base;

          return (
            <Link
              href={route(params.data?.id)}
              className="text-center text-color-primary"
            >
              <Maximize2 />
            </Link>
          );
        },
        ...colProps,
      };
    },
    type(colProps: ColDef = {}): ColDef {
      return {
        field: "type",
        headerName: "Type",
        filter: "agSetColumnFilter",
        filterParams: {
          values: ["lead", "opportunity"],
          cellRenderer: (params: any) => {
            return capitalize(params.value);
          },
        },
        cellRenderer: (params: ICellRendererParams<any, string>) => {
          return capitalize(params.data?.type);
        },
        ...colProps,
      };
    },
    entityName(colProps: ColDef = {}): ColDef {
      return {
        field: "name",
        headerName: "Lead/Opportunity Name",
        cellRenderer: (params: ICellRendererParams<any, string>) => {
          const type = (params.data as any).type;
          const entity = params.data;
          const route =
            type === "lead" ? routes.lead.base : routes.opportunity.base;

          return (
            <Link
              prefetch={false}
              href={route(params.data?.id)}
              className="text-color-primary"
            >
              {entity.name}
            </Link>
          );
        },
        filter: "agTextColumnFilter",
        ...colProps,
      };
    },
    customer(colProps: ColDef = {}): ColDef {
      return {
        field: "companyName",
        headerName: "Customer",
        valueFormatter: (
          params: ValueFormatterParams<MarketingSummaryResponse>
        ) => {
          if (typeof params.value === "string") {
            return params.value;
          } else {
            return (params.value as AccountResponse)?.name;
          }
        },
        valueGetter: (params) => {
          const entity = params.data;
          return entity?.companyName;
        },
        cellRenderer: (
          params: ICellRendererParams<MarketingSummaryResponse, string>
        ) => {
          const type = (params.data as any)?.type;
          const entity = params.data;

          if (type === "opportunity") {
            return (
              <Link
                prefetch={false}
                href={routes.account.base(entity?.accountId)}
                className="text-color-primary"
              >
                {entity?.companyName}
              </Link>
            );
          } else {
            return entity?.companyName;
          }
        },
        filter: "agTextColumnFilter",
        ...colProps,
      };
    },
    fsgOwner(colProps: ColDef = {}): ColDef {
      return {
        field: "fsgOwnerName",
        headerName: "FSG Owner",
        filter: "agTextColumnFilter",
        valueFormatter: (
          params: ValueFormatterParams<MarketingSummaryResponse>
        ) => {
          return params.value?.fullName;
        },
        cellRenderer: (
          params: ICellRendererParams<MarketingSummaryResponse, string>
        ) => {
          const entity = params.data;
          const value = entity.fsgOwnerName;

          return parseValue({
            value,
            component: (
              <Link
                prefetch={false}
                href={routes.contact.base(entity.fsgOwnerId)}
                className="text-color-primary"
              >
                {value}
              </Link>
            ),
          });
        },
      };
    },
    division(divisions: Division[], colProps: ColDef = {}): ColDef {
      return {
        field: "division",
        headerName: "Division",
        filter: "agSetColumnFilter",
        filterParams: {
          values: divisions.map(
            (div: { name: string; number: string }) => div.number
          ),
          valueFormatter: (params: ValueFormatterParams) =>
            divisionFormatter(params, divisions),
        },
        valueFormatter: (params: ValueFormatterParams) =>
          divisionFormatter({ value: params?.data?.division }, divisions),
        cellRenderer: (params: ICellRendererParams) => {
          return parseValue({
            value: params?.data?.division,
            component: (
              <>
                {divisionFormatter(
                  { value: params?.data?.division },
                  divisions
                )}
              </>
            ),
          });
        },
        sortable: true,
        ...colProps,
      };
    },
    source(data: MarketingSource[], colProps: ColDef = {}): ColDef {
      return {
        field: "source",
        headerName: "Source",
        filter: "agSetColumnFilter",
        filterParams: {
          values: data.map(({ source }) => source),
        },
        cellRenderer: (params: ICellRendererParams) =>
          parseValue({ value: params.data?.source }),
        ...colProps,
      };
    },
    campaign(colProps: ColDef = {}): ColDef {
      return {
        field: "campaignName",
        headerName: "Campaign",
        filter: "agTextColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, string>) => {
          return parseValue({
            value: params.data?.campaignName,
            component: (
              <Link
                prefetch={false}
                href={routes.campaign.base(params.data?.campaignId)}
                className="text-color-primary"
              >
                {params.data?.campaignName}
              </Link>
            ),
          });
        },
        ...colProps,
      };
    },
    status(filters: any[], colProps: ColDef = {}): ColDef {
      return {
        field: "status",
        headerName: "Status",
        filter: "agSetColumnFilter",
        filterParams: {
          values: filters,
          cellRenderer: (params: any) => {
            return normalizeENUM(params.value);
          },
        },
        cellRenderer: (
          params: ICellRendererParams<MarketingSummaryResponse>
        ) => {
          const type = (params.data as any)?.type;
          const entity = params.data;
          const value =
            type === "lead" ? entity.status : normalizeENUM(entity.status);

          return parseValue({ value, component: value });
        },
        ...colProps,
      };
    },
    estimatedValue(colProps: ColDef = {}): ColDef {
      return {
        field: "estimatedValue",
        headerName: "Estimated Value",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, number>) => {
          const value = params.data?.estimatedValue;
          return parseValue({
            value,
            component: (
              <FinancialText {...financialProps}>
                {value?.toString()}
              </FinancialText>
            ),
          });
        },
        valueFormatter: (
          params: ValueFormatterParams<MarketingSummaryResponse>
        ) => {
          return params.value
            ? parseFloat(params.value).toFixed(2).toString()
            : placeholder;
        },
        comparator: numericSort,
        ...colProps,
      };
    },
    probableValue(colProps: ColDef = {}): ColDef {
      return {
        field: "probableValue",
        headerName: "Probable $",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, number>) => {
          const entity = params.data as any;
          const type = entity.type;
          let value = entity.probableValue;
          if (type === "opportunity") {
            return parseValue({
              value,
              component: (
                <FinancialText {...financialProps}>
                  {value?.toString()}
                </FinancialText>
              ),
            });
          }
          return "N/A";
        },
        valueFormatter: (
          params: ValueFormatterParams<MarketingSummaryResponse>
        ) => {
          return params.value
            ? parseFloat(params.value).toFixed(2).toString()
            : placeholder;
        },
        comparator: numericSort,
        ...colProps,
      };
    },
    quoted(colProps: ColDef = {}): ColDef {
      return {
        field: "quotedValue",
        headerName: "Quoted $",
        filter: "agNumberColumnFilter",
        cellRenderer: (params: ICellRendererParams<any, number>) => {
          const value = params.data.quotedValue;
          return parseValue({
            value,
            component: (
              <FinancialText {...financialProps}>
                {value?.toString()}
              </FinancialText>
            ),
          });
        },
        comparator: numericSort,
        ...colProps,
      };
    },
  };
}

// Cache for filter values to maintain reference stability
const filterValuesCache = new Map<string, string[]>();
